import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from localStorage on mount and validate token
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('medicareToken');
      const storedUser = localStorage.getItem('medicareUser');
      
      if (storedToken && storedUser) {
        // Set token immediately for potential API calls
        setToken(storedToken);
        
        try {
          // Validate token by calling /api/auth/me
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${storedToken}` }
          });

          if (response.ok) {
            // Token is valid - use fresh user data from server
            const freshUserData = await response.json();
            setUser(freshUserData);
            localStorage.setItem('medicareUser', JSON.stringify(freshUserData));
          } else {
            // Token is invalid/expired - clear auth state
            console.warn('Stored token is invalid, clearing session');
            localStorage.removeItem('medicareToken');
            localStorage.removeItem('medicareUser');
            setToken(null);
            setUser(null);
          }
        } catch (error) {
          // Network error - use stored user data but log warning
          console.warn('Could not validate token, using stored data:', error);
          setUser(JSON.parse(storedUser));
        }
      }
      
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // Login function - calls real API
  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.detail || 'Login failed' };
      }

      // Store token and user
      localStorage.setItem('medicareToken', data.token);
      localStorage.setItem('medicareUser', JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);

      return { success: true, user: data.user };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Register function - calls real API
  const register = async (firstName, lastName, email, password, role = 'customer', affiliateCode = null) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email,
          password,
          role,
          affiliate_code: affiliateCode
        })
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.detail || 'Registration failed' };
      }

      // Store token and user
      localStorage.setItem('medicareToken', data.token);
      localStorage.setItem('medicareUser', JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);

      return { success: true, user: data.user };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem('medicareToken');
    localStorage.removeItem('medicareUser');
    setToken(null);
    setUser(null);
  }, []);

  // Fetch current user profile
  const fetchProfile = useCallback(async () => {
    if (!token) return null;

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        localStorage.setItem('medicareUser', JSON.stringify(userData));
        return userData;
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
    }
    return null;
  }, [token]);

  // Update user profile
  const updateProfile = async (updates) => {
    if (!token) return { success: false, error: 'Not authenticated' };

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        await fetchProfile();
        return { success: true };
      }

      const data = await response.json();
      return { success: false, error: data.detail || 'Update failed' };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  // Apply to become affiliate
  const applyForAffiliate = async (website, socialMedia, marketingPlan) => {
    if (!token || !user) return { success: false, error: 'Not authenticated' };

    try {
      const response = await fetch(`${API_URL}/api/affiliate/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          website,
          social_media: socialMedia,
          marketing_plan: marketingPlan
        })
      });

      const data = await response.json();

      if (response.ok) {
        await fetchProfile();
        return { success: true, affiliate_code: data.affiliate_code };
      }

      return { success: false, error: data.detail || 'Application failed' };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  // Role helpers
  const isAdmin = user?.role === 'admin';
  const isCustomer = user?.role === 'customer' || user?.role === 'admin';
  const isAffiliate = user?.is_affiliate === true;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        isAuthenticated: !!user,
        isAdmin,
        isCustomer,
        isAffiliate,
        login,
        register,
        logout,
        fetchProfile,
        updateProfile,
        applyForAffiliate
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
